import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import DOMPurify from 'dompurify';
import {
  Envelope, ArrowsClockwise, MagnifyingGlass, Eye, CheckCircle, XCircle, Clock,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  queued: 'bg-slate-100 text-slate-700',
  sent: 'bg-green-100 text-green-700',
  opened: 'bg-blue-100 text-blue-700',
  failed: 'bg-red-100 text-red-700',
  delivered: 'bg-teal-100 text-teal-700',
  bounced: 'bg-orange-100 text-orange-700',
};

const STATUS_LABELS = {
  queued: 'In coda', sent: 'Consegnata', opened: 'Aperta',
  failed: 'Non consegnata', delivered: 'Consegnata', bounced: 'Rimbalzata',
};

const SOURCE_LABELS = {
  pre_contract: 'Pre-contratto',
  campaign: 'Campagna',
  campaign_test: 'Test',
  manual: 'Manuale',
};

export default function MarketingCommunicationsPage() {
  const [data, setData] = useState({ items: [], total: 0, by_status: {} });
  const [loading, setLoading] = useState(true);
  // Pre-populate search filter from URL ?ragione=... so links from PreContracts
  // "Apri in Marketing Comunicazioni" land already filtered by client name.
  const initialQ = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('ragione') || '';
    } catch { return ''; }
  })();
  const [filters, setFilters] = useState({ source: '', status: '', q: initialQ });
  const [detail, setDetail] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.source) params.append('source', filters.source);
      if (filters.status) params.append('status', filters.status);
      if (filters.q) params.append('q', filters.q);
      params.append('limit', '200');
      const res = await fetch(`${API}/api/marketing/communications?${params}`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const viewDetail = async (id) => {
    try {
      const res = await fetch(`${API}/api/marketing/communications/${id}`, { credentials: 'include' });
      if (res.ok) setDetail(await res.json());
    } catch (e) { toast.error('Errore'); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="marketing-communications-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Envelope size={32} weight="duotone" className="text-orange-500" />
            Comunicazioni
          </h1>
          <p className="text-slate-500 mt-1">Log unificato di tutte le email inviate (pre-contratti + campagne).</p>
        </div>
        <Button variant="outline" onClick={fetchData}><ArrowsClockwise size={18} className="mr-2" /> Aggiorna</Button>
      </div>

      <div className="grid grid-cols-5 gap-3 mb-4">
        {['sent', 'opened', 'failed', 'queued'].map(s => (
          <div key={s} className="bg-white rounded-lg border p-3">
            <div className="text-xs text-slate-500">{STATUS_LABELS[s]}</div>
            <div className="text-2xl font-bold">{data.by_status[s] || 0}</div>
          </div>
        ))}
        <div className="bg-slate-50 rounded-lg border p-3">
          <div className="text-xs text-slate-500">Totale</div>
          <div className="text-2xl font-bold">{data.total}</div>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-3 mb-4 flex gap-3 items-center">
        <div className="flex-1 relative">
          <MagnifyingGlass size={16} className="absolute left-2 top-3 text-slate-400" />
          <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                 placeholder="Cerca per email, ragione sociale, oggetto..." className="pl-8" data-testid="comms-search" />
        </div>
        <select value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })}
                className="border rounded px-2 py-2 text-sm" data-testid="source-filter">
          <option value="">Tutte le origini</option>
          <option value="pre_contract">Pre-contratto</option>
          <option value="campaign">Campagna</option>
          <option value="campaign_test">Test campagna</option>
        </select>
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="border rounded px-2 py-2 text-sm" data-testid="status-filter">
          <option value="">Tutti gli stati</option>
          <option value="queued">In coda</option>
          <option value="sent">Consegnata</option>
          <option value="opened">Aperta</option>
          <option value="failed">Non consegnata</option>
        </select>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="max-h-[600px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0 border-b">
              <tr>
                <th className="text-left p-3">Destinatario</th>
                <th className="text-left p-3">Oggetto</th>
                <th className="text-left p-3">Origine</th>
                <th className="text-left p-3">Stato</th>
                <th className="text-left p-3">Inviata</th>
                <th className="text-left p-3">Aperta</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="7" className="p-6 text-center text-slate-400">Caricamento...</td></tr>}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan="7" className="p-6 text-center text-slate-400">Nessuna comunicazione</td></tr>
              )}
              {data.items.map(c => (
                <tr key={c.id} className="border-t hover:bg-slate-50">
                  <td className="p-3">
                    <div className="font-medium">{c.ragione_sociale || '—'}</div>
                    <div className="text-xs text-slate-500">{c.to_email}</div>
                  </td>
                  <td className="p-3">{c.subject}</td>
                  <td className="p-3">
                    <Badge variant="outline" className="text-xs">{SOURCE_LABELS[c.source] || c.source}</Badge>
                  </td>
                  <td className="p-3">
                    <Badge className={STATUS_COLORS[c.status] || ''}>
                      {c.status === 'sent' && <CheckCircle size={12} className="inline mr-1" />}
                      {c.status === 'opened' && <Eye size={12} className="inline mr-1" />}
                      {c.status === 'failed' && <XCircle size={12} className="inline mr-1" />}
                      {c.status === 'queued' && <Clock size={12} className="inline mr-1" />}
                      {STATUS_LABELS[c.status] || c.status}
                    </Badge>
                  </td>
                  <td className="p-3 text-xs text-slate-600">{fmt(c.sent_at)}</td>
                  <td className="p-3 text-xs text-slate-600">
                    {c.opened_at ? (
                      <span>{fmt(c.opened_at)} {c.open_count > 1 && <span className="text-blue-600">({c.open_count}x)</span>}</span>
                    ) : '—'}
                  </td>
                  <td className="p-3">
                    <Button size="sm" variant="outline" onClick={() => viewDetail(c.id)} data-testid={`view-comm-${c.id}`}>
                      <Eye size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* DETAIL DIALOG */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Dettaglio comunicazione</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><strong>Destinatario:</strong> {detail.ragione_sociale || '—'}</div>
                <div><strong>Email:</strong> {detail.to_email}</div>
                <div><strong>Oggetto:</strong> {detail.subject}</div>
                <div><strong>Origine:</strong> {SOURCE_LABELS[detail.source] || detail.source}</div>
                <div><strong>Stato:</strong> <Badge className={STATUS_COLORS[detail.status]}>{STATUS_LABELS[detail.status]}</Badge></div>
                <div><strong>Inviata:</strong> {fmt(detail.sent_at)}</div>
                <div><strong>Aperta:</strong> {fmt(detail.opened_at)} {detail.open_count > 0 && `(${detail.open_count}x)`}</div>
                <div><strong>Ultima apertura:</strong> {fmt(detail.last_opened_at)}</div>
              </div>
              {detail.attachments?.length > 0 && (
                <div>
                  <strong>Allegati:</strong>
                  <ul className="list-disc ml-6 text-xs">
                    {detail.attachments.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
              {detail.error && (
                <div className="bg-red-50 border border-red-200 p-2 rounded text-red-700 text-xs">
                  <strong>Errore:</strong> {detail.error}
                </div>
              )}
              <div>
                <strong>Corpo email:</strong>
                <div className="border rounded p-3 bg-slate-50 mt-2 max-h-80 overflow-y-auto"
                     dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(detail.body_html || '') }} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
